import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { logger } from '@/lib/logger';
import type { Organization, Member } from '@/types/clm';

interface OrganizationContextType {
  currentOrg: Organization | null;
  organizations: Organization[];
  currentMember: Member | null;
  isLoading: boolean;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentMember, setCurrentMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user's organizations
  const loadOrganizations = async () => {
    if (!user || !profile) {
      setOrganizations([]);
      setCurrentOrg(null);
      setCurrentMember(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // MULTI-ORG: Buscar organizações do usuário via user_organizations
      const { data: userOrgs, error: userOrgsError } = await supabase
        .from('user_organizations')
        .select(`
          *,
          organization:organizations(*)
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('joined_at', { ascending: false });

      if (userOrgsError) {
        logger.error('Error loading user_organizations', userOrgsError);
        // Fallback: tentar buscar org do profile (backward compatibility)
        if (profile.org_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', profile.org_id)
            .single();

          if (org) {
            setOrganizations([org]);
            setCurrentOrg(org);
            const fakeMember: Member = {
              id: profile.id,
              user_id: user.id,
              org_id: org.id,
              role: profile.is_admin ? 'admin' : 'viewer',
              permissions: {},
              invited_by: null,
              invited_at: null,
              joined_at: new Date().toISOString(),
              is_active: profile.is_active,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setCurrentMember(fakeMember);
            localStorage.setItem('currentOrgId', org.id);
          }
        }
        setIsLoading(false);
        return;
      }

      // Se nao tem organizacoes, verificar fallback
      if (!userOrgs || userOrgs.length === 0) {
        logger.warn('User has no organizations in user_organizations');

        // Fallback: tentar buscar org do profile (backward compatibility)
        if (profile.org_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', profile.org_id)
            .single();

          if (org) {
            setOrganizations([org]);
            setCurrentOrg(org);
            const fakeMember: Member = {
              id: profile.id,
              user_id: user.id,
              org_id: org.id,
              role: profile.is_admin ? 'admin' : 'viewer',
              permissions: {},
              invited_by: null,
              invited_at: null,
              joined_at: new Date().toISOString(),
              is_active: profile.is_active,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setCurrentMember(fakeMember);
            localStorage.setItem('currentOrgId', org.id);
          }
        } else {
          setOrganizations([]);
          setCurrentOrg(null);
          setCurrentMember(null);
        }
        setIsLoading(false);
        return;
      }

      // Mapear organizações (extrair do join)
      const orgs = userOrgs
        .map(uo => uo.organization)
        .filter((org): org is Organization => org !== null);

      setOrganizations(orgs);

      // Determinar org atual: usar localStorage, ou default, ou primeira
      let selectedOrg: Organization | null = null;
      const savedOrgId = localStorage.getItem('currentOrgId');

      if (savedOrgId) {
        // Verificar se usuário ainda tem acesso a essa org
        const savedUserOrg = userOrgs.find(uo => uo.org_id === savedOrgId);
        if (savedUserOrg && savedUserOrg.organization) {
          selectedOrg = savedUserOrg.organization as Organization;
        }
      }

      // Se não encontrou, usar org padrão (is_default = true)
      if (!selectedOrg) {
        const defaultUserOrg = userOrgs.find(uo => uo.is_default === true);
        if (defaultUserOrg && defaultUserOrg.organization) {
          selectedOrg = defaultUserOrg.organization as Organization;
        }
      }

      // Se ainda não encontrou, usar primeira org
      if (!selectedOrg && orgs.length > 0) {
        selectedOrg = orgs[0];
      }

      setCurrentOrg(selectedOrg);

      // Criar member baseado no user_organizations
      if (selectedOrg) {
        const userOrgData = userOrgs.find(uo => uo.org_id === selectedOrg!.id);
        if (userOrgData) {
          const member: Member = {
            id: userOrgData.id,
            user_id: user.id,
            org_id: selectedOrg.id,
            role: userOrgData.role || 'viewer',
            permissions: {},
            invited_by: null,
            invited_at: null,
            joined_at: userOrgData.joined_at || new Date().toISOString(),
            is_active: userOrgData.is_active,
            created_at: userOrgData.created_at || new Date().toISOString(),
            updated_at: userOrgData.updated_at || new Date().toISOString(),
          };
          setCurrentMember(member);
        }
        localStorage.setItem('currentOrgId', selectedOrg.id);
      }

    } catch (error) {
      logger.error('Error loading organizations', error);
      setOrganizations([]);
      setCurrentOrg(null);
      setCurrentMember(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Switch to a different organization
  const switchOrganization = async (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    if (!org || !user) return;

    setCurrentOrg(org);
    localStorage.setItem('currentOrgId', orgId);

    // Atualizar currentMember para a nova org
    try {
      const { data: userOrg } = await supabase
        .from('user_organizations')
        .select('*')
        .eq('user_id', user.id)
        .eq('org_id', orgId)
        .single();

      if (userOrg) {
        const member: Member = {
          id: userOrg.id,
          user_id: user.id,
          org_id: orgId,
          role: userOrg.role || 'viewer',
          permissions: {},
          invited_by: null,
          invited_at: null,
          joined_at: userOrg.joined_at || new Date().toISOString(),
          is_active: userOrg.is_active,
          created_at: userOrg.created_at || new Date().toISOString(),
          updated_at: userOrg.updated_at || new Date().toISOString(),
        };
        setCurrentMember(member);
      }
    } catch (error) {
      logger.error('Error updating currentMember', error);
    }
  };

  // Refresh organizations list
  const refreshOrganizations = async () => {
    await loadOrganizations();
  };

  // Load organizations when user or profile changes
  useEffect(() => {
    loadOrganizations();
  }, [user?.id, profile?.org_id]);

  return (
    <OrganizationContext.Provider
      value={{
        currentOrg,
        organizations,
        currentMember,
        isLoading,
        switchOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
